import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import Button from "./Button";
import Icon from "../AppIcon";

const NFCCodeModal = ({ isOpen, onClose, cardData }) => {
  const [nfcCode, setNfcCode] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyCode, setCopyCode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes in seconds

  useEffect(() => {
    if (isOpen && cardData) {
      setIsLoading(true);
      // Simulate secure code retrieval
      setTimeout(() => {
        const code =
          cardData.nfcCode ||
          `NFC${Math.random().toString(36).substr(2, 8).toUpperCase()}`;
        setNfcCode(code);
        // Generate QR code URL (in real app, this would be from a QR service)
        setQrCodeUrl(cardData?.qrcode);
        setIsLoading(false);
        setTimeRemaining(300); // Reset timer
      }, 1000);
    }
  }, [isOpen, cardData]);

  useEffect(() => {
    let interval;
    if (isOpen && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            onClose(); // Auto-close when timer expires
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isOpen, timeRemaining, onClose]);

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error("Failed to copy code:", error);
    }
  };
  const handleCopyCodeCard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopyCode(true);
      setTimeout(() => setCopyCode(false), 2000);
    } catch (error) {
      console.error("Failed to copy code:", error);
    }
  };

  const handleDownloadQR = () => {
    const link = document.createElement("a");
    link.href = qrCodeUrl;
    link.download = `${cardData?.hcId || "My Card"}.png`;
    // link.download = `nfc-qr-${cardData?.id || "code"}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleClose = () => {
    setNfcCode("");
    setQrCodeUrl("");
    setCopySuccess(false);
    setIsLoading(true);
    setTimeRemaining(300);
    onClose();
  };

  if (!cardData) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="NFC Code Access"
      size="md"
      closeOnOverlayClick={false}
    >
      <div className="p-6">
        {/* Card Information */}
        <div className="bg-surface border border-border rounded-lg p-4 mb-6">
          {cardData?.content && (
            <div className="flex items-center space-x-3 mb-3">
              <Icon name="CreditCard" size={20} className="text-accent" />
              <div>
                <h3 className="font-medium text-text-primary">
                  {cardData?.content?.name}
                </h3>
                <p className="text-sm text-text-muted">
                  {cardData?.content?.title}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-4 text-sm">
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                cardData.status === 0
                  ? "bg-success/20 text-success"
                  : "bg-text-muted/20 text-text-muted"
              }`}
            >
              {cardData.status === 0 ? "Active" : "Inactive"}
            </span>
            <span className="text-text-muted">
              Created: {new Date(cardData.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/20 rounded-full mb-4">
              <Icon
                name="Loader2"
                size={24}
                className="text-accent animate-spin"
              />
            </div>
            <p className="text-text-muted">Retrieving secure NFC code...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* NFC Code Display */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                NFC Access Code
              </label>
              <div className="bg-background border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <code className="font-mono text-lg font-medium text-text-primary tracking-wider">
                    {cardData?.hcId}
                  </code>
                  <Button
                    variant="ghost"
                    onClick={() => handleCopyCode(cardData?.hcId)}
                    className="p-2"
                  >
                    {copySuccess ? (
                      <Icon name="Check" size={18} className="text-success" />
                    ) : (
                      <Icon name="Copy" size={18} />
                    )}
                  </Button>
                </div>
                {copySuccess && (
                  <p className="text-success text-sm mt-2 flex items-center">
                    <Icon name="Check" size={14} className="mr-1" />
                    Code copied to clipboard
                  </p>
                )}
              </div>
              <div className="bg-background border border-border rounded-lg p-4 mt-1">
                <div className="flex items-center justify-between">
                  <code className="font-mono text-lg font-medium text-text-primary tracking-wider">
                    {cardData?.cardUrl}
                  </code>
                  <Button
                    variant="ghost"
                    onClick={() => handleCopyCode(cardData?.cardUrl)}
                    className="p-2"
                  >
                    {copySuccess ? (
                      <Icon name="Check" size={18} className="text-success" />
                    ) : (
                      <Icon name="Copy" size={18} />
                    )}
                  </Button>
                </div>
                {copySuccess && (
                  <p className="text-success text-sm mt-2 flex items-center">
                    <Icon name="Check" size={14} className="mr-1" />
                    Code copied to clipboard
                  </p>
                )}
              </div>
        
            </div>

            {/* QR Code */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                QR Code
              </label>
              <div className="bg-white rounded-lg p-4 inline-block">
                <img
                  src={qrCodeUrl}
                  alt="NFC QR Code"
                  className="w-48 h-48 mx-auto"
                  onError={(e) => {
                    e.target.src = "/assets/images/no_image.png";
                  }}
                />
              </div>
              <div className="mt-3">
                <Button
                  variant="outline"
                  onClick={handleDownloadQR}
                  iconName="Download"
                  iconPosition="left"
                >
                  Download QR Code
                </Button>
              </div>
            </div>

             <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                NFC Password
              </label>
             
              <div className="bg-background border border-border rounded-lg p-4 mt-2">
                <div className="flex items-center justify-between">
                  <code className="font-mono text-lg font-medium text-text-primary tracking-wider">
                    {cardData?.cardCode}
                  </code>
                  <Button
                    variant="ghost"
                    onClick={()=>handleCopyCodeCard(cardData?.cardCode)}
                    className="p-2"
                  >
                    {copyCode ? (
                      <Icon name="Check" size={18} className="text-success" />
                    ) : (
                      <Icon name="Copy" size={18} />
                    )}
                  </Button>
                </div>
                {copyCode && (
                  <p className="text-success text-sm mt-2 flex items-center">
                    <Icon name="Check" size={14} className="mr-1" />
                    Code copied to clipboard
                  </p>
                )}
              </div>
            </div>

            {/* Usage Instructions */}
            <div className="bg-surface border border-border rounded-lg p-4">
              <h4 className="font-medium text-text-primary mb-2 flex items-center">
                <Icon name="Info" size={16} className="mr-2" />
                Usage Instructions
              </h4>
              <ul className="text-sm text-text-muted space-y-1">
                <li>• Use this code to program NFC tags or devices</li>
                <li>• Share the QR code for quick mobile access</li>
                <li>• Code expires automatically for security</li>
                <li>• Contact support if you need extended access</li>
              </ul>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NFCCodeModal;
